import * as React from 'react'
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import type { TeamDto, TeamInvitationDto, TenantActivityListDto } from '@lib/dto/team.dto'
import type { TenantRole } from '@lib/enums/tenant-role.enum'
import type { InviteMemberInput } from '@lib/input/team.input'
import type { Uuid } from '@lib/validators/params'
import { useDashboardScope } from '@/dashboard/DashboardScope'
import { useRefreshOnForbidden } from '@/dashboard/useRefreshOnForbidden'
import { TeamService } from '@/services/TeamService'
import { teamKeys } from '@/hooks/queryKeys'

/** The team service bound to the current dashboard session (tenant user or staff view-as). */
function useScopedTeamService(): TeamService {
  const { client } = useDashboardScope()
  return React.useMemo(() => new TeamService(client), [client])
}

export function useTeam(): UseQueryResult<TeamDto> {
  const { tenantId, cacheKey } = useDashboardScope()
  const service = useScopedTeamService()
  return useQuery({
    queryKey: teamKeys.detail(tenantId, cacheKey),
    queryFn: () => service.get(tenantId),
  })
}

/** The organization's activity log. Owners only; pass `enabled: false` for anyone else. */
export function useTenantActivity(enabled = true): UseQueryResult<TenantActivityListDto> {
  const { tenantId, cacheKey } = useDashboardScope()
  const service = useScopedTeamService()
  return useQuery({
    queryKey: teamKeys.activity(tenantId, cacheKey),
    queryFn: () => service.activity(tenantId),
    enabled,
  })
}

/** Owner actions on the team. Each refreshes the team and activity when it settles. */
export function useTeamActions(): {
  invite: UseMutationResult<TeamInvitationDto, Error, InviteMemberInput>
  resend: UseMutationResult<TeamInvitationDto, Error, Uuid>
  revoke: UseMutationResult<void, Error, Uuid>
  changeRole: UseMutationResult<TeamDto, Error, { userId: Uuid; role: TenantRole }>
  remove: UseMutationResult<void, Error, Uuid>
} {
  const { tenantId, cacheKey } = useDashboardScope()
  const service = useScopedTeamService()
  const queryClient = useQueryClient()
  const onError = useRefreshOnForbidden()
  const onSettled = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: teamKeys.detail(tenantId, cacheKey) }),
      queryClient.invalidateQueries({ queryKey: teamKeys.activity(tenantId, cacheKey) }),
    ])
  }
  return {
    invite: useMutation({ mutationFn: (input) => service.invite(tenantId, input), onError, onSettled }),
    resend: useMutation({ mutationFn: (id) => service.resend(tenantId, id), onError, onSettled }),
    revoke: useMutation({ mutationFn: (id) => service.revoke(tenantId, id), onError, onSettled }),
    changeRole: useMutation({
      mutationFn: ({ userId, role }) => service.changeRole(tenantId, userId, { role }),
      onError,
      onSettled,
    }),
    remove: useMutation({ mutationFn: (userId) => service.remove(tenantId, userId), onError, onSettled }),
  }
}
